import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import CurrencyInput from '../inputs/CurrencyInput';
import useTips, { TipOption } from '../../hooks/order/useTips';
import { Collection } from 'collect.js';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { useTranslation } from 'react-i18next';
import OrderPaymentBreakdown from '../order/OrderPaymentBreakdown';

interface Item extends TipOption {
  is_selected: boolean;
  amount: number;
}

export default function Tips() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Tips' });

  const { formatCurrency } = useAppMethods();
  const { options, calculate } = useTips();
  const { data, localData, update } = useOrderFlow();

  const [activeIndex, setActiveIndex] = useState(null);
  const [customTipAmount, setCustomTipAmount] = useState<number>(localData.tip);

  const items = useMemo<Collection<Item>>(
    () =>
      options.map((item) => ({
        ...item,
        is_selected: activeIndex === item.index,
        amount: item.is_custom ? customTipAmount : calculate(data.sub_total, item),
      })),
    [options, activeIndex, data.sub_total, customTipAmount],
  );

  const predefinedItems = useMemo<Item[]>(
    () => items.filter((item: Item) => !item.is_zero && !item.is_custom).toArray(),
    [items],
  );

  const zeroOption = items.firstWhere('is_zero', true);
  const customOption = items.firstWhere('is_custom', true);
  const selectedOption = items.firstWhere('is_selected', true);

  useEffect(() => {
    const getActiveOption = () => {
      if (localData.tip === undefined) {
        return items.firstWhere('is_default', true);
      }

      if (!data.sub_total) {
        return null;
      }

      if (selectedOption) {
        return selectedOption;
      }

      if (localData.tip === 0) {
        return options.firstWhere('is_zero', true);
      }

      return options.first(
        (item) => calculate(data.sub_total, item) === localData.tip,
        options.firstWhere('is_custom', true),
      );
    };

    const option = getActiveOption();

    option && onSelect(option);
  }, [data.sub_total]);

  const onChangeCustomTip = (value: number) => {
    setCustomTipAmount(value);
    update('tip', value);
  };

  const onSelect = (option: Item) => {
    setActiveIndex(option.index);
    update('tip', option?.is_custom ? customTipAmount : calculate(data.sub_total, option));
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-brand-text-primary">{t('header.title')}</p>
        <span className="text-[11px] text-muted-foreground">{t('header.subtitle')}</span>
      </div>
      {/*
        `data-selected` and `data-tip-amount` make a tip pill assertable without reading its label.
        The label is either a percentage or translated copy ("Other"/"None"), and the selected state
        is carried purely by background-colour classes — so a spec could otherwise only assert on
        Tailwind class names, which is exactly the coupling `data-test` exists to avoid.

        `data-tip-amount` is the CLIENT-computed figure (useTips.calculate on the draft subtotal). The
        totals marker's `data-tip` is the BACKEND's echo of it from the order draft. Asserting the two
        agree is a real round trip, not a tautology — they come from different sources.
      */}
      <div className="flex gap-xs flex-wrap">
        {predefinedItems.map((item) => (
          <button
            key={`tip-option-${item.index}`}
            type="button"
            data-test={`tip-option-${item.percentage}`}
            data-selected={item.is_selected ? 'true' : 'false'}
            data-tip-amount={item.amount}
            onClick={() => onSelect(item)}
            className={classnames(pillBase, item.is_selected ? pillActive : pillInactive)}
          >
            {item.percentage}%
          </button>
        ))}
        <button
          type="button"
          data-test="tip-option-other"
          data-selected={customOption.is_selected ? 'true' : 'false'}
          data-tip-amount={customOption.amount}
          onClick={() => onSelect(customOption)}
          className={classnames(pillBase, customOption.is_selected ? pillActive : pillInactive)}
        >
          {t('buttons.other')}
        </button>
        <button
          type="button"
          data-test="tip-option-none"
          data-selected={zeroOption.is_selected ? 'true' : 'false'}
          data-tip-amount={zeroOption.amount}
          onClick={() => onSelect(zeroOption)}
          className={classnames(pillBase, zeroOption.is_selected ? pillActive : pillInactive)}
        >
          {t('buttons.none')}
        </button>
      </div>

      {customOption.is_selected && (
        <div className="mt-sm">
          <CurrencyInput
            id="custom-tip-input"
            testId="custom-tip-input"
            value={customOption.amount}
            onChange={onChangeCustomTip}
          />
        </div>
      )}
      {/* A percentage of a large order can pass the cap too, not only a typed amount. */}
      {selectedOption?.amount > 500 && (
        <p data-test="custom-tip-max-warning" className="text-danger text-xs">
          {t('form.custom_tip.validation.max', { value: formatCurrency(500) })}
        </p>
      )}
      {selectedOption?.amount > 0 && (
        <OrderPaymentBreakdown.Row name={t('summary_label')} amount={selectedOption.amount} />
      )}
    </>
  );
}

const pillBase = 'flex-1 py-xs rounded-full text-xs font-semibold transition-colors';

const pillActive = 'bg-brand-primary text-brand-button-primary-text';

const pillInactive = 'bg-muted text-muted-foreground hover:text-brand-text-primary';
